// Waves primitive (+ the domainWarp helper it composes with) — plan §4.1.
// Used by sand/snow ripples, marble veins and wood rings.
//
// NOTE (repo hazard): no backticks and no GLSL-ES-300 reserved identifiers (flat/smooth/...)
// inside the template literal.
//
// `domainWarpP` is a p-TRANSFORMER, not a layer: it is exposed as a prelude function (the
// `domainWarp` registry entry is implemented + NOT layer-usable) and is composed INSIDE the
// pattern primitives that need it (waves takes warpScale/warpAmount args).  A standalone
// "warp layer" would have to mutate the layer stack's sample position, which the layer model
// (each layer = pure vec2 -> vec4(color, height)) deliberately does not allow.
export const WAVES_GLSL = `
// Domain warp: offset p by two decorrelated fbm lookups (meters).  amount is in meters, so
// the warp is scale-explicit like every other length in a recipe.
vec2 domainWarpP(vec2 p, float warpScale, int octaves, float amount) {
  if (amount <= 0.0) return p;
  float wx = fbmVal(p + vec2(17.13, 91.7), warpScale, octaves, 2.0, 0.5, 3);
  float wz = fbmVal(p + vec2(-53.7, 41.3), warpScale, octaves, 2.0, 0.5, 3);
  return p + vec2(wx, wz) * amount;
}

// Directional wave train.  wavelength in meters; angleDeg = the propagation direction in the
// world XZ plane; sharpness > 1 narrows the crests (sand ripples), 1 = pure sinusoid.
// Returns [0, 1] with mean ~0.5.
// Set3 §3.4 — the optional SECOND train: crossAngleDeg + crossStrength (0..1) + a
// crossWavelengthRatio that MUST be incommensurate (~1.6-1.8, not 1.0) or the two trains beat
// into a moire lattice rather than meandering (see the note at the mix below).
// sharpness stays the SAME train's sharpness; the cross train is a symmetric sinusoid.
// Set3 §3.4.3 — the asymmetric crest profile: with asymm > 1 the crest is steeper on the LEE
// side (the side facing +dir) and gentler on the STOSS side — a ridge, not a rope.  This is
// the t -> f(t) remap: t^asymm, then mixed back by 1/asymm (the inverse-power remap that
// restores the [0,1] span without the DC bias a plain pow would introduce).
// AA (plan Section 7): when the wavelength falls below ~2px the train fades toward its mean
// (0.5) instead of sizzling.  Spec-clean smoothstep edges (edge0 < edge1) — the plan's
// inverted form is undefined in GLSL ES 300.
float wavesVal(vec2 p, float wavelength, float angleDeg, float sharpness, float crossAngleDeg, float crossStrength, float crossWavelengthRatio, float asymm) {
  float wl = max(wavelength, 1e-5);
  float a = angleDeg * 0.01745329252;
  vec2 dir = vec2(cos(a), sin(a));
  float v = 0.5 + 0.5 * sin(dot(p, dir) * 6.28318530718 / wl);
  if (crossStrength > 0.0) {
    float ca = crossAngleDeg * 0.01745329252;
    vec2 cdir = vec2(cos(ca), sin(ca));
    // The cross train MUST be incommensurate with the main one.  At an equal wavelength the two
    // beat into a rigid moire lattice of spacing wl/(2 sin(delta/2)) — visible as a regular grid
    // of blobs, the opposite of the meandering crests the cross train exists to produce.
    float cwl = max(wl * max(crossWavelengthRatio, 0.05), 1e-5);
    float cv = 0.5 + 0.5 * sin(dot(p, cdir) * 6.28318530718 / cwl);
    v = mix(v, cv, clamp(crossStrength, 0.0, 1.0));
  }
  v = pow(clamp(v, 0.0, 1.0), max(sharpness, 0.05));
  if (asymm > 1.0) {
    float k = clamp(asymm, 1.0, 8.0);
    v = pow(v, k) * (1.0 / k) + v * (1.0 - 1.0 / k);
  }
  float fade = smoothstep(1.0 * matPixelSize(), 2.0 * matPixelSize(), wl);
  return mix(0.5, v, fade);
}

// Set11 Phase 1 — analytic derivative of wavesVal (the sinusoid chain, differentiated exactly;
// the domain warp treated as LOCALLY CONSTANT — its Jacobian is close to identity by design,
// warpScale ~ 5-15x the wavelength, so the error is a few percent of the gradient).  The
// returned gradient is the derivative of the RAW v BEFORE the pixel fade (the fade is a
// p-independent multiplier of v's deviation from 0.5 — d(mix(0.5, v, fade))/dp = fade * dv/dp).
// The caller multiplies by heightScale itself (per-metre result).
vec2 wavesValD(vec2 p, float wavelength, float angleDeg, float sharpness, float crossAngleDeg, float crossStrength, float crossWavelengthRatio, float asymm) {
  float wl = max(wavelength, 1e-5);
  float a = angleDeg * 0.01745329252;
  vec2 dir = vec2(cos(a), sin(a));
  float k = 6.28318530718 / wl;
  float phase = dot(p, dir) * k;
  float v0 = 0.5 + 0.5 * sin(phase);
  vec2 dv0 = 0.5 * cos(phase) * k * dir;
  if (crossStrength > 0.0) {
    float ca = crossAngleDeg * 0.01745329252;
    vec2 cdir = vec2(cos(ca), sin(ca));
    float cwl = max(wl * max(crossWavelengthRatio, 0.05), 1e-5);
    float ck = 6.28318530718 / cwl;
    float cphase = dot(p, cdir) * ck;
    float cv = 0.5 + 0.5 * sin(cphase);
    vec2 dcv = 0.5 * cos(cphase) * ck * cdir;
    float cs = clamp(crossStrength, 0.0, 1.0);
    v0 = mix(v0, cv, cs);
    dv0 = mix(dv0, dcv, cs);
  }
  // pow(v, sharpness): clamp the base in DERIVATIVE expressions (pow(v, sharp-1) at v == 0 is
  // inf -> NaN normal).  The value channel keeps the original clamped pow.
  float vv = clamp(v0, 0.0, 1.0);
  float vp = pow(vv, max(sharpness, 0.05));
  vec2 dvp = dv0 * (max(sharpness, 0.05) * pow(max(vv, 1e-4), max(sharpness, 0.05) - 1.0));
  if (asymm > 1.0) {
    float kk = clamp(asymm, 1.0, 8.0);
    vp = pow(vp, kk) * (1.0 / kk) + vp * (1.0 - 1.0 / kk);
    dvp = dvp * (pow(max(vp, 1e-4), kk - 1.0) + (1.0 - 1.0 / kk));
  }
  return dvp;
}
`;
